import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  FileText, 
  Heart, 
  LogOut,
  Settings,
  MessageCircle,
  ChevronRight,
  Clock,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Sparkles
} from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '../../context/AuthContext';
import { useChatSessions } from '../../contexts/ChatContext';
import { ChatSession } from '../../entities';
import { hasMinimumAdminAccess } from '../../types/roles';

interface MobileMenuProps {
  onNavigate?: () => void;
  onViewAllSessions?: () => void;
  onProfileClick?: () => void;
  onFeedbackClick?: () => void;
  favoritesCount?: number;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
  onNavigate,
  onViewAllSessions,
  onProfileClick,
  onFeedbackClick,
  favoritesCount = 0,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { sessions, selectSession } = useChatSessions();
  const [isUserSectionExpanded, setIsUserSectionExpanded] = useState(false);

  // Get 5 most recent sessions
  const recentSessions = React.useMemo(() => {
    return [...sessions]
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 5);
  }, [sessions]);

  const navigation = [
    { name: 'Chat', href: '/chat', icon: MessageSquare },
    { name: 'My Information', href: '/user-facts', icon: FileText },
    { name: 'Vitality', href: '/vitality', icon: Sparkles },
    { 
      name: 'My Resources', 
      href: '/favorites', 
      icon: Heart, 
      badge: favoritesCount > 0 ? favoritesCount : undefined 
    },
  ];

  // Check if user has admin portal access
  const hasAdminAccess = hasMinimumAdminAccess(user?.roles);

  const handleSessionClick = (session: ChatSession) => {
    selectSession(session.id);
    // Navigate to chat page if not already there
    if (location.pathname !== '/chat') {
      navigate('/chat');
    }
    if (onNavigate) {
      onNavigate();
    }
  };

  const formatSessionTime = (date: string) => {
    const now = new Date();
    const sessionDate = new Date(date);
    const diffMs = now.getTime() - sessionDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return sessionDate.toLocaleDateString();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Primary Navigation */}
      <div className="px-2 pt-4 pb-3">
        <h3 className="px-4 text-xs font-semibold text-rm-neutral-600 uppercase tracking-wider mb-2">
          Navigation
        </h3>
        <div className="space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onNavigate}
                className={clsx(
                  'flex items-center px-4 py-3 rounded-lg text-base font-medium transition-colors',
                  'touch-target',
                  isActive
                    ? 'bg-rm-accent-500 text-rm-ink-900'
                    : 'text-rm-ink-900 hover:bg-rm-bg-50'
                )}
              >
                <Icon className="w-5 h-5 mr-3 flex-shrink-0" />
                <span className="flex-1">{item.name}</span>
                {'badge' in item && item.badge !== undefined && (
                  <span className={clsx(
                    'ml-2 px-2 py-0.5 text-xs font-semibold rounded-full',
                    isActive 
                      ? 'bg-rm-primary-700 text-white' 
                      : 'bg-rm-accent-500 text-rm-ink-900'
                  )}>
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </Link>
            );
          })}
          
          {/* HR Admin Portal Link */}
          {hasAdminAccess && (
            <Link
              to="/admin"
              onClick={onNavigate}
              className={clsx(
                'flex items-center px-4 py-3 rounded-lg text-base font-medium transition-colors',
                'touch-target',
                location.pathname.startsWith('/admin')
                  ? 'bg-rm-accent-500 text-rm-ink-900'
                  : 'text-rm-ink-900 hover:bg-rm-bg-50'
              )}
            >
              <BarChart3 className="w-5 h-5 mr-3 flex-shrink-0" />
              <span className="flex-1">HR</span>
            </Link>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-rm-neutral-600/20 my-2" />

      {/* Recent Sessions - Scrollable section */}
      <div className="px-2 py-3 overflow-y-auto flex-1 min-h-0">
        <div className="flex items-center justify-between px-4 mb-2">
          <h3 className="text-xs font-semibold text-rm-neutral-600 uppercase tracking-wider">
            Recent Sessions
          </h3>
        </div>
        
        {recentSessions.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-rm-neutral-600">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No recent sessions</p>
            <p className="text-xs mt-1">Start a conversation to see it here</p>
          </div>
        ) : (
          <>
            <div className="space-y-1">
              {recentSessions.map((session) => (
                <button
                  key={session.id}
                  onClick={() => handleSessionClick(session)}
                  className={clsx(
                    'w-full flex items-start px-4 py-3 rounded-lg text-left transition-colors',
                    'touch-target hover:bg-rm-bg-50'
                  )}
                >
                  <MessageCircle className="w-4 h-4 mt-0.5 mr-3 flex-shrink-0 text-rm-neutral-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-rm-ink-900 truncate">
                      {session.title || 'New Conversation'}
                    </p>
                    <div className="flex items-center mt-1 text-xs text-rm-neutral-600">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatSessionTime(session.updatedAt)}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-rm-neutral-600 flex-shrink-0 ml-2" />
                </button>
              ))}
            </div>
            
            {/* View All Sessions Button */}
            {sessions.length > 5 && (
              <div className="mt-3 px-4">
                <button
                  onClick={() => {
                    onViewAllSessions?.();
                    onNavigate?.();
                  }}
                  className={clsx(
                    'w-full px-4 py-2.5 rounded-lg text-sm font-medium',
                    'border border-rm-accent-500 text-rm-accent-500',
                    'hover:bg-rm-accent-500 hover:text-rm-ink-900',
                    'transition-colors touch-target'
                  )}
                >
                  View All Sessions ({sessions.length})
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-rm-neutral-600/20" />

      {/* User Profile Section - Expandable, always visible at bottom */}
      <div className="px-2 py-4 safe-bottom flex-shrink-0">
        <button
          onClick={() => setIsUserSectionExpanded(!isUserSectionExpanded)}
          className="w-full px-4 mb-2 touch-target"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-rm-accent-500 flex items-center justify-center text-rm-ink-900 font-semibold">
              {user?.displayName?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-semibold text-rm-ink-900 truncate">
                {user?.displayName || 'User'}
              </p>
              <p className="text-xs text-rm-neutral-600 truncate">
                {user?.email || ''}
              </p>
            </div>
            {isUserSectionExpanded ? (
              <ChevronUp className="w-5 h-5 text-rm-neutral-600 flex-shrink-0" />
            ) : (
              <ChevronDown className="w-5 h-5 text-rm-neutral-600 flex-shrink-0" />
            )}
          </div>
        </button>

        {/* Expanded User Options */}
        {isUserSectionExpanded && (
          <div className="space-y-1 mt-2 pb-6">
            <button
              onClick={() => {
                onProfileClick?.();
                onNavigate?.();
              }}
              className={clsx(
                'w-full flex items-center px-4 py-2.5 rounded-lg text-sm font-medium',
                'touch-target text-rm-ink-900 hover:bg-rm-bg-50 transition-colors'
              )}
            >
              <Settings className="w-4 h-4 mr-3" />
              Profile Settings
            </button>

            <button
              onClick={() => {
                onFeedbackClick?.();
                onNavigate?.();
              }}
              className={clsx(
                'w-full flex items-center px-4 py-2.5 rounded-lg text-sm font-medium',
                'touch-target text-rm-ink-900 hover:bg-rm-bg-50 transition-colors'
              )}
            >
              <MessageCircle className="w-4 h-4 mr-3" />
              App Feedback
            </button>

            <button
              onClick={() => {
                signOut();
                onNavigate?.();
              }}
              className={clsx(
                'w-full flex items-center px-4 py-2.5 rounded-lg text-sm font-medium',
                'touch-target text-red-600 hover:bg-red-50 transition-colors'
              )}
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
